import {
  countries,
  FormFieldDropdownInput,
  FormFieldInput,
  LoadingIndicator,
  NotificationBar,
  NotificationMood,
  TaxTypeIndia,
  FormFieldTypeaheadInput,
  usePersistForm,
  whitelistedStatusCodes,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FieldError, FieldErrors, Resolver, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { State, City } from 'country-state-city';

import { verifyBillingAddress } from '../../common/api/sign-up';
import { ApiErrorResponse } from '../../common/api/types';
import { useSignUp } from '../../common/hooks/useSignUp';
import { SignUpStep } from '../../common/provider/SignUpContext';
import { isCountryIndia, stepRouteMapping } from '../signUpUtility';
import { ActionBar } from './ActionBar';
import { BillingForm, StateDto } from './types';
import {
  SIGN_UP_BILLING_DETAIL_FORM,
  SIGN_UP_TAX_TYPE,
} from '../../common/constants';
import { Header } from './Header';
import { InputLabelTitle } from './InputLabelTitle';

const billingFormResolver: Resolver<BillingForm> = (values) => {
  const errors = {} as FieldErrors<BillingForm>;

  if (!values.address) {
    errors.address = {
      message: 'Address Line 1 is required',
    } as FieldError;
  } else if (values.address.length === 1) {
    errors.address = {
      message: 'Address must be greater than 1 character',
    } as FieldError;
  } else if (values.address.length > 100) {
    errors.address = {
      message: 'Address must be fewer than 100 characters',
    } as FieldError;
  }

  if (values.address2?.length > 0 && values.address2?.length < 3) {
    errors.address2 = {
      message: 'Address must be greater than 3 characters',
    } as FieldError;
  } else if (values.address2?.length > 100) {
    errors.address2 = {
      message: 'Address must be fewer than 100 characters',
    } as FieldError;
  }

  if (!values.state) {
    errors.state = {
      message: 'State is required',
    } as FieldError;
  }

  if (!values.city) {
    errors.city = {
      message: 'City is required',
    } as FieldError;
  }

  if (!values.zipcode && values.country === 'IN') {
    errors.zipcode = {
      message: 'Pin code is required',
    } as FieldError;
  } else if (!values.zipcode && values.country !== 'IN') {
    errors.zipcode = {
      message: 'Zip code is required',
    } as FieldError;
  } else if (
    values.country === 'IN' &&
    !new RegExp(/^[1-9]{1}[0-9]{2}\s{0,1}[0-9]{3}$/i).test(values.zipcode)
  ) {
    errors.zipcode = {
      message: 'Invalid pin',
    } as FieldError;
  } else if (
    values.country !== 'IN' &&
    !new RegExp(/^(\d{5})(?:[-\s](\d{4}))?$/i).test(values.zipcode)
  ) {
    errors.zipcode = {
      message: 'Invalid zip',
    } as FieldError;
  }

  return { values, errors };
};

export const Billing: FunctionComponent = () => {
  const navigate = useNavigate();
  const {
    formId,
    setSignUpStep,
    setErrorMessage,
    signUpCustomerCountry,
    areBillingDetailsValid,
    setAreBillingDetailsValid,
    gstAddress,
  } = useSignUp();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [stateList, setStateList] = useState<StateDto[]>();
  const [cityList, setCityList] = useState<string[]>([]);
  const [typeaheadSelected, setTypeaheadSelected] = useState<string[]>();

  const countryIsIndia = useMemo(() => {
    return isCountryIndia(signUpCustomerCountry as string);
  }, [signUpCustomerCountry]);

  const isGstTaxType = useMemo(() => {
    const rawData = sessionStorage.getItem(SIGN_UP_TAX_TYPE);
    if (rawData && JSON.parse(rawData) === TaxTypeIndia[TaxTypeIndia.GST]) {
      return true;
    }
    return false;
  }, []);

  const getSavedData = useCallback(() => {
    const rawData = sessionStorage.getItem(SIGN_UP_BILLING_DETAIL_FORM);
    if (rawData) {
      const dataObject: BillingForm = JSON.parse(rawData);
      return dataObject;
    }
    return {
      country: signUpCustomerCountry,
      address: '',
      address2: '',
      city: '',
      state: '',
      zipcode: gstAddress?.pinCode.toString() || '',
    };
  }, [signUpCustomerCountry, gstAddress]);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    getValues,
    watch,
    setValue,
    control,
    formState: { isValid, errors },
  } = useForm<BillingForm>({
    mode: 'onChange',
    defaultValues: getSavedData(),
    resolver: billingFormResolver,
  });
  const selectedState = watch('state');
  const selectedCity = watch('city');

  useEffect(() => {
    if (selectedState) {
      const citiesInState = City.getCitiesOfState(
        signUpCustomerCountry as string,
        selectedState
      );
      const cityNames = citiesInState.map((city) => {
        return city.name;
      });
      if (cityNames) {
        setCityList(cityNames);
      }
    }
  }, [selectedState, signUpCustomerCountry]);

  useEffect(() => {
    const statesInCountry = State.getStatesOfCountry(signUpCustomerCountry);
    if (statesInCountry) {
      const stateNames = statesInCountry.map((state) => {
        return {
          name: state.name,
          code: state.isoCode,
        };
      });
      setStateList(stateNames);
    }
    if (signUpCustomerCountry) {
      reset((formValues) => ({
        ...formValues,
        country: signUpCustomerCountry,
      }));
    }
  }, [signUpCustomerCountry, reset]);

  useEffect(() => {
    if (gstAddress && stateList) {
      const gstStateCode = stateList.find(
        (state) =>
          state.name.toLowerCase() ===
          gstAddress.state.replace('_', ' ').toLowerCase()
      );
      if (gstStateCode) {
        reset((formValues) => ({
          ...formValues,
          state: gstStateCode.code,
        }));
      }
    }
  }, [stateList, gstAddress, reset]);

  useEffect(() => {
    if (selectedCity) {
      setTypeaheadSelected([selectedCity]);
    } else {
      setTypeaheadSelected([]);
    }
  }, [selectedCity]);

  usePersistForm({
    value: getValues(),
    sessionStorageKey: SIGN_UP_BILLING_DETAIL_FORM,
  });

  const postalCodeTitle = useMemo(() => {
    if (countryIsIndia) {
      return 'Pin';
    } else {
      return 'Zip';
    }
  }, [countryIsIndia]);

  const navigateToNextPage = useCallback(() => {
    setAreBillingDetailsValid(true);
    setSignUpStep(SignUpStep.paymentDetails);
    navigate(stepRouteMapping[SignUpStep[SignUpStep.paymentDetails]]);
  }, [navigate, setAreBillingDetailsValid, setSignUpStep]);

  const onSubmit = handleSubmit(async (form: BillingForm) => {
    if (!areBillingDetailsValid) {
      try {
        setIsLoading(true);
        const response = await verifyBillingAddress(form, formId as string);
        if (!response.validation.valid) {
          response.validation.violations.forEach((violation) =>
            setError(violation.element as keyof BillingForm, {
              message: violation.violation,
            })
          );
        } else if (response) {
          navigateToNextPage();
        }
        setIsLoading(false);
      } catch (e: any) {
        if (whitelistedStatusCodes.includes(e.request.status)) {
          const errorResponse: ApiErrorResponse = JSON.parse(
            e.request.response
          );
          setErrorMessage(errorResponse.error.description);
        } else {
          setErrorMessage('Please go back to the form and try again');
        }
        setIsLoading(false);
      }
    } else {
      navigateToNextPage();
    }
  });

  const clearCityValue = useCallback(() => {
    setValue('city', '');
  }, [setValue]);

  const stateAndCityFormInput = useMemo(() => {
    if (!stateList) {
      return null;
    }
    if (signUpCustomerCountry === 'IN') {
      return (
        <div className="flex flex-row w-full gap-x-4">
          <FormFieldDropdownInput
            title={
              <InputLabelTitle
                title="State"
                hasError={Boolean(errors?.state)}
                required
              />
            }
            className="w-2/5 pb-2"
            errors={errors}
            inputKey="state"
            options={stateList.map(({ code, name }) => ({
              id: code,
              display: name,
            }))}
            disabled={isGstTaxType}
            inputClassName={`${isGstTaxType ? 'opacity-30' : ''} shadow`}
            onChange={clearCityValue}
            register={register}
            required={true}
            testId="state-select"
          />
          <div className="w-3/5">
            <FormFieldTypeaheadInput
              control={control}
              title={
                <InputLabelTitle
                  title="City"
                  hasError={Boolean(errors?.city)}
                  required
                />
              }
              inputKey="city"
              options={cityList}
              disabled={!selectedState}
              defaultSelected={typeaheadSelected}
              selected={typeaheadSelected}
              required={true}
            />
          </div>
        </div>
      );
    }
    return (
      <div className="flex flex-row w-full gap-x-4">
        <FormFieldInput
          errors={errors}
          inputKey="city"
          register={register}
          registerProps={{
            required: true,
            onChange: () => {
              setAreBillingDetailsValid(false);
            },
          }}
          testId="companyName-input"
          className="w-full pb-4"
          inputClassName="shadow"
          title={
            <InputLabelTitle
              title="City"
              hasError={Boolean(errors?.city)}
              required
            />
          }
        />
        <div className="w-3/5">
          <FormFieldDropdownInput
            title={
              <InputLabelTitle
                title="State"
                hasError={Boolean(errors?.state)}
                required
              />
            }
            className="w-2/5 pb-2"
            errors={errors}
            inputKey="state"
            options={stateList.map(({ code, name }) => ({
              id: code,
              display: name,
            }))}
            register={register}
            required={true}
            testId="state-select"
          />
        </div>
      </div>
    );
  }, [
    signUpCustomerCountry,
    errors,
    control,
    selectedState,
    typeaheadSelected,
    cityList,
    stateList,
    isGstTaxType,
    register,
    clearCityValue,
    setAreBillingDetailsValid,
  ]);

  if (isLoading || !stateList) {
    return (
      <div className="flex flex-col w-full h-full justify-center items-center">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:h-[100vh] w-full content-between items-center">
      <Header
        subtitle="Enter your current address where you are normally billed."
        title="Billing Address"
      />
      <form className="flex-1 grid grid-cols-12 w-full pb-8 px-8 xl:px-0 font-opensans lg:overflow-auto mb-28 lg:mb-52">
        <div className="col-start-1 xl:col-start-2 col-span-10 flex flex-col gap-4 h-full">
          {isGstTaxType && (
            <div className="mb-6">
              <NotificationBar
                mood={NotificationMood.Neutral}
                message={
                  <span className="text-xs">
                    Defaulted to the registered GST number’s state and pincode.
                    If incorrect, go back and input a new GST number. Enter the
                    rest of the billing address to proceed.
                  </span>
                }
              />
            </div>
          )}
          <FormFieldDropdownInput
            title={
              <InputLabelTitle
                title="Country"
                hasError={Boolean(errors?.country)}
                required
              />
            }
            className="pb-2"
            errors={errors}
            inputKey="country"
            options={countries.map(({ code, name }) => ({
              id: code,
              display: name,
            }))}
            disabled={true}
            register={register}
            required={true}
            testId="country-select"
          />
          <FormFieldInput
            errors={errors}
            inputKey="address"
            register={register}
            registerProps={{
              required: true,
              onChange: () => {
                setAreBillingDetailsValid(false);
              },
            }}
            testId="companyName-input"
            className="w-full pb-4"
            inputClassName="shadow"
            title={
              <InputLabelTitle
                title="Address"
                hasError={Boolean(errors?.address)}
                required
              />
            }
          />
          <FormFieldInput
            errors={errors}
            inputKey="address2"
            register={register}
            registerProps={{
              required: false,
              onChange: () => {
                setAreBillingDetailsValid(false);
              },
            }}
            testId="companyName-input"
            className="w-full pb-4"
            inputClassName="shadow"
            title={
              <InputLabelTitle
                title="Address 2"
                hasError={Boolean(errors?.address2)}
                required={false}
              />
            }
          />
          {stateAndCityFormInput}
          <FormFieldInput
            errors={errors}
            inputKey="zipcode"
            register={register}
            registerProps={{
              required: true,
              onChange: () => {
                setAreBillingDetailsValid(false);
              },
            }}
            testId="zipcode-input"
            className="w-full pb-4"
            disabled={isGstTaxType}
            inputClassName={`${isGstTaxType ? 'opacity-30' : ''} shadow`}
            title={
              <InputLabelTitle
                title={postalCodeTitle}
                hasError={Boolean(errors?.zipcode)}
                required
              />
            }
          />
        </div>
      </form>
      <ActionBar onSubmit={onSubmit} isValid={isValid} />
    </div>
  );
};
